import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["percentage", "tooltip", "tooltipPosition", "tooltipDelay", "color", "className"];
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '../tooltip/Tooltip';
import OverlayTrigger from '../overlay/OverlayTrigger';
var LEFT = OverlayTrigger.LEFT;
var TOP = OverlayTrigger.TOP;
var RIGHT = OverlayTrigger.RIGHT;
var BOTTOM = OverlayTrigger.BOTTOM;
var SMALL = 'small';
var LARGE = 'large';
var LIGHT = 'light';
var BOLD = 'bold';
var renderProgressBar = function renderProgressBar(_ref, index, useProgressDivider) {
  var _ref$percentage = _ref.percentage,
    percentage = _ref$percentage === void 0 ? 0 : _ref$percentage,
    tooltip = _ref.tooltip,
    _ref$tooltipPosition = _ref.tooltipPosition,
    tooltipPosition = _ref$tooltipPosition === void 0 ? TOP : _ref$tooltipPosition,
    _ref$tooltipDelay = _ref.tooltipDelay,
    tooltipDelay = _ref$tooltipDelay === void 0 ? 50 : _ref$tooltipDelay,
    color = _ref.color,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  var progressBarClassNames = classNames('progress-bar', color ? color : 'progress-bar-info', useProgressDivider && 'progress-divider', className && className);
  var progressBar = /*#__PURE__*/React.createElement("div", _extends({
    key: index,
    className: progressBarClassNames,
    style: {
      width: "".concat(percentage, "%")
    }
  }, props));
  if (tooltip) {
    var tooltipElement = /*#__PURE__*/React.createElement(Tooltip, {
      id: "tooltip-".concat(index),
      width: "auto"
    }, tooltip);
    return /*#__PURE__*/React.createElement(OverlayTrigger, {
      key: index,
      placement: tooltipPosition,
      overlay: tooltipElement,
      delay: tooltipDelay
    }, progressBar);
  }
  return progressBar;
};
var renderAllProgressBars = function renderAllProgressBars(progress, useProgressDivider) {
  if (!progress.length) {
    return null;
  }
  return progress.map(function (item, index) {
    return renderProgressBar(item, index, useProgressDivider);
  });
};
var getIconAlignment = function getIconAlignment(icon) {
  return icon.alignment || LEFT;
};
var getIconColorClassName = function getIconColorClassName(icon) {
  return icon.color || 'text-color-gray';
};
var getLabelAlignment = function getLabelAlignment(label) {
  return label.alignment || RIGHT;
};
var getLabelColorClassName = function getLabelColorClassName(label) {
  return label.color || 'text-color-dark';
};
var getLabelAlignmentClassName = function getLabelAlignmentClassName(label) {
  return label.alignment === TOP ? 'align-items-end' : 'align-items-center';
};
var getProgressBarHightClassName = function getProgressBarHightClassName(size) {
  return size === SMALL && 'height-5' || size === LARGE && 'height-10';
};
var getIsIconLeftAndLabelRight = function getIsIconLeftAndLabelRight(icon, iconAlignment, label, labelAlignment) {
  return icon.name && iconAlignment === LEFT && label.value && labelAlignment === RIGHT;
};
var getIsIconRightAndLabelLeft = function getIsIconRightAndLabelLeft(icon, iconAlignment, label, labelAlignment) {
  return icon.name && iconAlignment === RIGHT && label.value && labelAlignment === LEFT;
};
var getIsIconAndLabelLeft = function getIsIconAndLabelLeft(icon, iconAlignment, label, labelAlignment) {
  return icon.name && iconAlignment === LEFT && label.value && labelAlignment === LEFT;
};
var getIsIconAndLabelRight = function getIsIconAndLabelRight(icon, iconAlignment, label, labelAlignment) {
  return icon.name && iconAlignment === RIGHT && label.value && labelAlignment === RIGHT;
};
var getIsIconOnly = function getIsIconOnly(label, icon) {
  return !label.value && icon.name;
};
var getIsIconOnlyLeft = function getIsIconOnlyLeft(isIconOnly, iconAlignment) {
  return isIconOnly && iconAlignment === LEFT;
};
var getIsIconOnlyRight = function getIsIconOnlyRight(isIconOnly, iconAlignment) {
  return isIconOnly && iconAlignment === RIGHT;
};
var getIsLabelOnly = function getIsLabelOnly(label, icon) {
  return label.value && !icon.name;
};
var getIsLabelOnlyLeft = function getIsLabelOnlyLeft(isLabelOnly, labelAlignment) {
  return isLabelOnly && labelAlignment === LEFT;
};
var getIsLabelOnlyRight = function getIsLabelOnlyRight(isLabelOnly, labelAlignment) {
  return isLabelOnly && labelAlignment === RIGHT;
};
var renderIcon = function renderIcon(icon, size) {
  if (!icon.name) {
    return null;
  }
  var iconColor = getIconColorClassName(icon);
  var iconAlignment = getIconAlignment(icon);
  var iconWrapperClasses = classNames(iconAlignment === LEFT && 'flex-order-1 margin-right-5', iconAlignment === RIGHT && 'flex-order-2 margin-left-5');
  var iconClasses = classNames('rioglyph', icon.name && icon.name, iconColor, size === SMALL && 'text-size-h3', size === LARGE && 'text-size-h2', icon.className && icon.className);
  return /*#__PURE__*/React.createElement("div", {
    className: iconWrapperClasses
  }, /*#__PURE__*/React.createElement("span", {
    className: iconClasses
  }));
};
var renderLabel = function renderLabel(label, size, labelWrapperClasses) {
  if (!label.value) {
    return null;
  }
  var labelColor = getLabelColorClassName(label);
  var labelAlignment = getLabelAlignment(label);
  var labelClasses = classNames('display-flex', 'ellipsis-1', 'flex-1-0', 'justify-content-center', labelAlignment === TOP && 'width-0', labelColor, size === SMALL && 'text-size-16', size === LARGE && 'text-size-h3', label.weight !== LIGHT && 'text-bold', label.className && label.className);
  return /*#__PURE__*/React.createElement("div", {
    className: labelWrapperClasses
  }, /*#__PURE__*/React.createElement("span", {
    className: labelClasses
  }, label.value));
};

// eslint-disable-next-line complexity
var StatusBar = function StatusBar(props) {
  var _props$progress = props.progress,
    progress = _props$progress === void 0 ? [] : _props$progress,
    icon = props.icon,
    label = props.label,
    size = props.size,
    className = props.className,
    useProgressDivider = props.useProgressDivider;

  // Default values for icon and label alignment
  var iconAlignment = getIconAlignment(icon);
  var labelAlignment = getLabelAlignment(label);
  var isIconLeftAndLabelRight = getIsIconLeftAndLabelRight(icon, iconAlignment, label, labelAlignment);
  var isIconRightAndLabelLeft = getIsIconRightAndLabelLeft(icon, iconAlignment, label, labelAlignment);
  var isIconAndLabelLeft = getIsIconAndLabelLeft(icon, iconAlignment, label, labelAlignment);
  var isIconAndLabelRight = getIsIconAndLabelRight(icon, iconAlignment, label, labelAlignment);
  var isIconOnly = getIsIconOnly(label, icon);
  var isIconOnlyLeft = getIsIconOnlyLeft(isIconOnly, iconAlignment);
  var isIconOnlyRight = getIsIconOnlyRight(isIconOnly, iconAlignment);
  var isLabelOnly = getIsLabelOnly(label, icon);
  var isLabelOnlyLeft = getIsLabelOnlyLeft(isLabelOnly, labelAlignment);
  var isLabelOnlyRight = getIsLabelOnlyRight(isLabelOnly, labelAlignment);
  var wrapperClasses = classNames('StatusBar', 'width-100pct', 'display-flex', getLabelAlignmentClassName(label), className);
  var progressLabelWrapperClasses = classNames('display-flex', 'flex-1-0', labelAlignment === TOP && 'flex-order-1 flex-column margin-bottom-10', isIconAndLabelRight && 'flex-order-1 align-items-center', isIconAndLabelLeft && 'flex-order-2 align-items-center', isIconLeftAndLabelRight && 'flex-order-2 align-items-center', isIconRightAndLabelLeft && 'flex-order-1 align-items-center', isLabelOnlyLeft && 'align-items-center', isLabelOnlyRight && 'align-items-center', isIconOnlyLeft && 'flex-order-2 align-items-center', isIconOnlyRight && 'flex-order-1 align-items-center');
  var labelWrapperClasses = classNames(labelAlignment === TOP && 'display-flex justify-content-center', isIconAndLabelLeft && 'flex-order-1 margin-right-10', isIconAndLabelRight && 'flex-order-2 margin-left-10', isIconLeftAndLabelRight && 'flex-order-2 margin-left-10', isIconRightAndLabelLeft && 'flex-order-1 margin-right-10', isLabelOnlyLeft && 'flex-order-1 margin-right-10', isLabelOnlyRight && 'flex-order-2 margin-left-10');
  var progressOuterWrapperClasses = classNames('display-flex', 'flex-1-0', isIconAndLabelLeft && 'flex-order-2', isIconAndLabelRight && 'flex-order-1', isIconLeftAndLabelRight && 'flex-order-1', isIconRightAndLabelLeft && 'flex-order-2', isLabelOnlyLeft && 'flex-order-2', isLabelOnlyRight && 'flex-order-1');
  var progressWrapperClasses = classNames('progress', 'margin-0', 'flex-1-0', getProgressBarHightClassName(size));
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClasses
  }, renderIcon(icon, size), /*#__PURE__*/React.createElement("div", {
    className: progressLabelWrapperClasses
  }, renderLabel(label, size, labelWrapperClasses), /*#__PURE__*/React.createElement("div", {
    className: progressOuterWrapperClasses
  }, /*#__PURE__*/React.createElement("div", {
    className: progressWrapperClasses
  }, renderAllProgressBars(progress, useProgressDivider)))));
};
StatusBar.defaultProps = {
  size: SMALL,
  useProgressDivider: true,
  icon: {
    name: '',
    alignment: LEFT
  },
  label: {
    value: '',
    alignment: RIGHT,
    weight: BOLD
  },
  progress: [],
  className: ''
};
StatusBar.propTypes = {
  icon: PropTypes.shape({
    name: PropTypes.string.isRequired,
    alignment: PropTypes.oneOf([LEFT, RIGHT]),
    color: PropTypes.string,
    className: PropTypes.string
  }),
  label: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    alignment: PropTypes.oneOf([LEFT, TOP, RIGHT]),
    weight: PropTypes.oneOf([LIGHT, BOLD]),
    color: PropTypes.string,
    className: PropTypes.string
  }),
  progress: PropTypes.arrayOf(PropTypes.shape({
    percentage: PropTypes.number.isRequired,
    tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    tooltipPosition: PropTypes.oneOf([TOP, BOTTOM, LEFT, RIGHT]),
    tooltipDelay: PropTypes.number,
    color: PropTypes.string,
    className: PropTypes.string
  })),
  useProgressDivider: PropTypes.bool,
  size: PropTypes.oneOf([SMALL, LARGE]),
  className: PropTypes.string
};
export default StatusBar;